import { useEffect, useRef } from 'react';

/** Carbon renders a ComboBox/Dropdown menu inline, as a child of the list-box.
 *  Inside a modal that means the menu is trapped in the modal's stacking context
 *  and the footer paints over it — z-index cannot help, the footer is a later
 *  sibling in a `display: grid` container that establishes its own context.
 *
 *  This hook relocates the open menu node to a host element outside the modal
 *  (but still inside React's root — see `hostParentFor`) and positions it against
 *  its field with fixed coordinates, then puts it back where it came from on
 *  close. The node itself is moved rather than re-rendered, so Downshift's
 *  listeners stay attached — and its outside-click check tests
 *  `menuRef.contains(target)` against this very node, so clicking an option still
 *  selects rather than dismissing.
 *
 *  Detection is a MutationObserver on `class` changes: Carbon toggles
 *  `--list-box--expanded` on the field when the menu opens. That keeps the hook
 *  independent of how many ComboBoxes the container holds. */

const EXPANDED_SELECTOR = '.bx--list-box--expanded, .cds--list-box--expanded';

/* Opt-out. A dropdown inside a marked subtree keeps Carbon's own inline menu,
   the way the mock renders it. Portaling suits a form field, whose position is
   stable; it does not suit one inside a scrolling table, where Carbon flips the
   menu upward (`--list-box--up`) when the row sits low in the viewport while
   this hook positions for a downward menu — the menu then lands over the
   header instead of against its field. */
const INLINE_MENU_OPT_OUT = '[data-inline-list-box]';
const MENU_SELECTOR = '.bx--list-box__menu, .cds--list-box__menu';

/** Carbon caps the menu at 5.5 items, so the ceiling depends on the field's size
 *  modifier (_list-box.scss: `--list-box--expanded` and its `--sm`/`--lg`
 *  variants). Those rules select through the expanded field, so a portaled menu
 *  loses them and has to carry the cap inline. */
const MENU_MAX_HEIGHT = { sm: 176, md: 220, lg: 264 };

/* The host must stay inside React's root container: React 17 delegates events
   there rather than on `document`, so a node appended straight to `<body>` gets
   hover styling but no `onClick` — Downshift would never see the selection.
   `[title-chaining-stage]` is what entrypoints/application.js renders into. */
const hostParentFor = (container: HTMLElement): HTMLElement => {
  const stage = container.closest<HTMLElement>('[title-chaining-stage]');
  if (stage) return stage;

  // No stage (tests, or a future change of mount point) — settle for the
  // outermost element still under <body>, which is inside the render root.
  let node = container;
  while (node.parentElement && node.parentElement !== document.body) {
    node = node.parentElement;
  }
  return node.parentElement ? node : document.body;
};

const maxHeightFor = (field: HTMLElement) => {
  const cx = field.classList;
  if (cx.contains('bx--list-box--sm') || cx.contains('cds--list-box--sm')) {
    return MENU_MAX_HEIGHT.sm;
  }
  if (cx.contains('bx--list-box--lg') || cx.contains('cds--list-box--lg')) {
    return MENU_MAX_HEIGHT.lg;
  }
  return MENU_MAX_HEIGHT.md;
};

interface Placement {
  /** The expanded list-box the hosted menu belongs to. */
  field: HTMLElement;
  menu: HTMLElement;
  /** Where to put the menu back — captured before the move. */
  parent: HTMLElement;
  nextSibling: ChildNode | null;
  /** Last coordinates written, so the per-frame check can skip no-op writes. */
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Keep a gap to the viewport edge so the menu never sits flush against it. */
const EDGE_GAP = 8;

export const usePortaledListBoxMenu = <T extends HTMLElement>(
  enabled: boolean,
  hostClassName: string
) => {
  const containerRef = useRef<T>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!enabled || !container) return undefined;

    const host = document.createElement('div');
    host.className = hostClassName;
    hostParentFor(container).appendChild(host);

    let placed: Placement | null = null;

    const restore = () => {
      if (!placed) return;
      const { menu, parent, nextSibling } = placed;
      menu.style.cssText = '';
      parent.insertBefore(menu, nextSibling);
      placed = null;
    };

    const position = (field: HTMLElement, menu: HTMLElement) => {
      const rect = field.getBoundingClientRect();
      const cap = maxHeightFor(field);

      // Open downward by default, but flip above when the menu would run past
      // the bottom of the viewport and there is more room up there. Fields low
      // in a long table — the last rows of the inventory picker — otherwise push
      // the menu out through the modal's footer.
      const wanted = Math.min(cap, menu.scrollHeight || cap);
      const below = window.innerHeight - rect.bottom - EDGE_GAP;
      const above = rect.top - EDGE_GAP;
      // Compare against the height the menu actually wants. Testing a smaller
      // floor here reads "it fits" while the menu still runs off the bottom.
      const flip = below < wanted && above > below;

      // Clamp to whichever side was chosen, so a menu that fits neither scrolls
      // internally rather than spilling past the viewport edge.
      //
      // `wanted` belongs in here, not just in the flip test. Without it a short
      // menu took the whole available space as its height — and since a flipped
      // menu is positioned at `rect.top - height`, a two-item menu opened a full
      // cap's distance above its field instead of sitting on it, reading as a
      // menu floating somewhere unrelated to the row it belongs to.
      const height = Math.min(cap, wanted, Math.max(flip ? above : below, 0));
      const top = flip ? rect.top - height : rect.bottom;

      // Write only on change: this runs every frame (see `track`), and assigning
      // identical values would still dirty style and invalidate layout.
      if (
        placed &&
        placed.top === top &&
        placed.left === rect.left &&
        placed.width === rect.width &&
        placed.height === height
      ) {
        return;
      }
      if (placed) {
        placed.top = top;
        placed.left = rect.left;
        placed.width = rect.width;
        placed.height = height;
      }

      menu.style.position = 'fixed';
      menu.style.top = `${top}px`;
      menu.style.left = `${rect.left}px`;
      menu.style.width = `${rect.width}px`;
      menu.style.maxBlockSize = `${height}px`;
    };

    const sync = () => {
      const expandedFields = [
        ...container.querySelectorAll<HTMLElement>(EXPANDED_SELECTOR),
      ].filter((candidate) => !candidate.closest(INLINE_MENU_OPT_OUT));

      /* Carbon gives every Dropdown its own Downshift instance and coordinates
         nothing between them, so opening a second one does not always close the
         first — in a table of them, clicking another row's field leaves the
         previous menu open. Two menus then compete: this hook portals whichever
         comes first in the DOM, so the one just clicked renders inline beside an
         unrelated row. Close the one we were already hosting and keep the new. */
      if (placed && expandedFields.length > 1) {
        const stale = placed.field;
        restore();
        stale.dispatchEvent(
          new KeyboardEvent('keydown', {
            key: 'Escape',
            code: 'Escape',
            keyCode: 27,
            bubbles: true,
          })
        );
        return;
      }

      const field = expandedFields[0];

      if (!field) {
        restore();
        return;
      }

      // Already hosting this field's menu. Moving the node out means it is no
      // longer a descendant of the field, so `placed` — not the DOM — is what
      // says whether this field is handled; just keep it aligned.
      if (placed?.field === field) {
        position(field, placed.menu);
        return;
      }

      // A different field opened (Carbon only keeps one menu open at a time).
      restore();

      const menu = field.querySelector<HTMLElement>(MENU_SELECTOR);
      if (!menu) return;

      placed = {
        field,
        menu,
        parent: menu.parentElement as HTMLElement,
        nextSibling: menu.nextSibling,
        top: NaN,
        left: NaN,
        width: NaN,
        height: NaN,
      };
      host.appendChild(menu);
      position(field, menu);
    };

    /* Re-align every frame while a menu is open, rather than on scroll/resize/
       mutation events. The field moves for reasons none of those catch: picking
       "From existing Type Curve" adds a field and reflows the form, an invalid
       message appears under a field, focusing an input makes the modal body
       scroll it into view, and the modal's own open transition is still settling.
       Any of those left the menu at the field's old coordinates, overlapping the
       input — the intermittent "coming over" report. Same approach as floating-ui's
       `autoUpdate({ animationFrame: true })`; it costs one rect read per frame and
       only while a menu is open, and `position` skips writes when nothing moved. */
    let frame = 0;
    const track = () => {
      if (placed) position(placed.field, placed.menu);
      frame = requestAnimationFrame(track);
    };
    frame = requestAnimationFrame(track);

    const observer = new MutationObserver(sync);
    observer.observe(container, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
    });
    sync();

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      restore();
      host.remove();
    };
  }, [enabled, hostClassName]);

  return containerRef;
};
